// LearnIndex.jsx - Index page for all educational content
import React from 'react';
import { Link } from 'react-router-dom';
import { getAllLandingPages } from '../../content/landingPages';
import LearnLayout from '../../layouts/LearnLayout';
import SEOHead from '../../components/seo/SEOHead';
import { BookOpen, BarChart3, Wrench, ArrowRight, Clock, ChevronRight, Shield, TrendingUp, Users } from 'lucide-react';

const TIER_CONFIG = {
  1: { label: 'Guides & Strategies', subtitle: 'Practical playbooks from the field', icon: BookOpen, color: 'blue', gradient: 'from-blue-600 to-indigo-700' },
  2: { label: 'Data & Research', subtitle: 'Numbers that drive better decisions', icon: BarChart3, color: 'purple', gradient: 'from-purple-600 to-violet-700' },
  3: { label: 'Tools & Solutions', subtitle: 'Technology that works for you', icon: Wrench, color: 'amber', gradient: 'from-amber-500 to-orange-600' }
};

function PageCard({ page, tierColor }) {
  const linkTo = page.tier === 3 ? `/solutions/${page.slug}` : `/learn/${page.slug}`;
  const colorMap = {
    blue: {
      hover: 'hover:border-blue-300 hover:shadow-blue-100/50',
      tag: 'bg-blue-50 text-blue-700',
      arrow: 'text-blue-500 group-hover:text-blue-600',
      accent: 'bg-blue-500'
    },
    purple: {
      hover: 'hover:border-purple-300 hover:shadow-purple-100/50',
      tag: 'bg-purple-50 text-purple-700',
      arrow: 'text-purple-500 group-hover:text-purple-600',
      accent: 'bg-purple-500'
    },
    amber: {
      hover: 'hover:border-amber-300 hover:shadow-amber-100/50',
      tag: 'bg-amber-50 text-amber-700',
      arrow: 'text-amber-500 group-hover:text-amber-600',
      accent: 'bg-amber-500'
    }
  };
  const colors = colorMap[tierColor] || colorMap.blue;

  return (
    <Link
      to={linkTo}
      className={`group relative bg-white rounded-xl border border-gray-200 p-6 transition-all duration-300 hover:shadow-lg ${colors.hover} hover:-translate-y-0.5`}
    >
      <div className={`absolute top-0 left-6 right-6 h-0.5 ${colors.accent} rounded-b opacity-0 group-hover:opacity-100 transition-opacity`} />
      <h3 className="font-bold text-[#172A3A] text-lg mb-2 group-hover:text-[#172A3A] leading-snug pr-8">
        {page.title}
      </h3>
      <p className="text-sm text-gray-500 mb-4 line-clamp-2 leading-relaxed">
        {page.metaDescription}
      </p>
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-xs text-gray-400">
          <Clock size={12} />
          {page.readTime} min read
        </span>
        <span className={`flex items-center gap-1 text-sm font-semibold ${colors.arrow} transition-all group-hover:gap-2`}>
          Read <ChevronRight size={14} />
        </span>
      </div>
    </Link>
  );
}

export default function LearnIndex() {
  const allPages = getAllLandingPages();
  const tier1 = allPages.filter(p => p.tier === 1);
  const tier2 = allPages.filter(p => p.tier === 2);
  const tier3 = allPages.filter(p => p.tier === 3);

  // Pick 3 featured articles (one from each tier)
  const featured = [tier1[0], tier2[0], tier3[0]].filter(Boolean);

  return (
    <LearnLayout>
      <SEOHead
        title="Medicare Retention Resources | Pro IRP"
        description="Free guides, data, and strategies for Medicare client retention. Learn how to reduce churn and protect your book of business."
        canonicalUrl="https://proirp.com/learn"
      />

      {/* Hero Section */}
      <div className="-mx-4 sm:-mx-6 -mt-10 sm:-mt-14 mb-16 bg-gradient-to-br from-[#172A3A] via-[#1e3a50] to-[#172A3A] text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#FFB800]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />

        <div className="max-w-4xl mx-auto px-6 sm:px-8 py-16 sm:py-20 relative">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FFB800]/20 rounded-full text-[#FFB800] text-sm font-medium mb-6">
            <span className="w-2 h-2 bg-[#FFB800] rounded-full" />
            Free Resources for Medicare Agents
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-6 leading-tight">
            Medicare Retention<br />
            <span className="text-[#FFB800]">Knowledge Center</span>
          </h1>
          <p className="text-xl text-blue-100/80 max-w-2xl leading-relaxed">
            {allPages.length} in-depth guides, research reports, and strategy playbooks written
            by industry professionals. Everything you need to protect your book of business.
          </p>

          {/* Quick stats */}
          <div className="flex flex-wrap gap-8 mt-10 pt-8 border-t border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <BookOpen size={18} className="text-blue-300" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{tier1.length}</div>
                <div className="text-xs text-blue-200/60">Strategy Guides</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <BarChart3 size={18} className="text-purple-300" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{tier2.length}</div>
                <div className="text-xs text-blue-200/60">Research Reports</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <Wrench size={18} className="text-amber-300" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{tier3.length}</div>
                <div className="text-xs text-blue-200/60">Solution Guides</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Featured Picks */}
      {featured.length > 0 && (
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-[#FFB800] rounded-xl flex items-center justify-center shadow-md">
              <TrendingUp className="w-5 h-5 text-[#172A3A]" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[#172A3A]">Start Here</h2>
              <p className="text-sm text-gray-500">Our most popular guides for getting started</p>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {featured.map((page, idx) => {
              const tierConfig = TIER_CONFIG[page.tier];
              return (
                <Link
                  key={page.slug}
                  to={page.tier === 3 ? `/solutions/${page.slug}` : `/learn/${page.slug}`}
                  className="group relative bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-xl hover:border-[#FFB800]/30 transition-all duration-300 hover:-translate-y-1"
                >
                  <div className={`h-32 bg-gradient-to-br ${tierConfig.gradient} relative overflow-hidden`}>
                    <div className="absolute inset-0 opacity-10" style={{
                      backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.3) 1px, transparent 1px)',
                      backgroundSize: '16px 16px'
                    }} />
                    <div className="absolute bottom-4 left-5">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white text-xs font-medium">
                        <tierConfig.icon size={12} />
                        {tierConfig.label}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-bold text-[#172A3A] mb-2 leading-snug group-hover:text-[#172A3A]">
                      {page.title}
                    </h3>
                    <p className="text-sm text-gray-500 mb-4 line-clamp-2 leading-relaxed">
                      {page.metaDescription}
                    </p>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400 flex items-center gap-1">
                        <Clock size={12} /> {page.readTime} min
                      </span>
                      <span className="text-[#172A3A] font-semibold flex items-center gap-1 group-hover:text-[#FFB800] group-hover:gap-2 transition-all">
                        Read <ArrowRight size={14} />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Tier 1: Educational */}
      <section className="mb-16">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-[#172A3A]">Guides & Strategies</h2>
            <p className="text-sm text-gray-500">Practical playbooks from the field</p>
          </div>
        </div>
        <p className="text-gray-600 mb-8 ml-[52px]">
          Actionable retention strategies covering every aspect of Medicare client management,
          from enrollment periods to commission protection.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          {tier1.map(page => (
            <PageCard key={page.slug} page={page} tierColor="blue" />
          ))}
        </div>
      </section>

      {/* Tier 2: Data */}
      <section className="mb-16">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-[#172A3A]">Data & Research</h2>
            <p className="text-sm text-gray-500">Numbers that drive better decisions</p>
          </div>
        </div>
        <p className="text-gray-600 mb-8 ml-[52px]">
          Industry analytics, churn data, and benchmarking reports to help you understand
          where your retention stands and where to improve.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          {tier2.map(page => (
            <PageCard key={page.slug} page={page} tierColor="purple" />
          ))}
        </div>
      </section>

      {/* Tier 3: Solutions */}
      <section className="mb-16">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
            <Wrench className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-[#172A3A]">Tools & Solutions</h2>
            <p className="text-sm text-gray-500">Technology that works for you</p>
          </div>
        </div>
        <p className="text-gray-600 mb-8 ml-[52px]">
          In-depth evaluations of retention software, automation tools, and platforms
          designed specifically for Medicare agencies.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          {tier3.map(page => (
            <PageCard key={page.slug} page={page} tierColor="amber" />
          ))}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="-mx-4 sm:-mx-6 mt-8 bg-gradient-to-br from-[#172A3A] via-[#1e3a50] to-[#172A3A] rounded-2xl p-10 sm:p-14 text-white text-center relative overflow-hidden">
        <div className="absolute top-0 right-0 w-80 h-80 bg-[#FFB800]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FFB800]/20 rounded-full text-[#FFB800] text-sm font-medium mb-6">
            <span className="w-2 h-2 bg-[#FFB800] rounded-full" />
            Built by an Agent, for Agents
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Knowledge Is Just the Beginning
          </h2>
          <p className="text-blue-100/80 mb-8 max-w-2xl mx-auto text-lg leading-relaxed">
            These guides give you the strategy. Pro IRP gives you the tools to execute it—AI-powered
            risk scoring, automated outreach, and real-time alerts to protect every client in your book.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/signup"
              className="inline-flex items-center justify-center gap-2 bg-[#FFB800] text-[#172A3A] font-bold px-8 py-4 rounded-xl hover:bg-[#e5a600] transition-all hover:scale-105 shadow-lg shadow-[#FFB800]/25"
            >
              Start Free Trial
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/blog"
              className="inline-flex items-center justify-center gap-2 border-2 border-white/30 text-white font-semibold px-8 py-4 rounded-xl hover:bg-white/10 transition-colors"
            >
              Read Our Blog
            </Link>
          </div>
        </div>
      </section>
    </LearnLayout>
  );
}
